import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'not-found'>('loading');
  const [message, setMessage] = useState('');
  const email = searchParams.get('email');
  const token = searchParams.get('token');

  useEffect(() => {
    const processUnsubscribe = async () => {
      if (!email || !token) {
        setStatus('error');
        setMessage('Link de cancelamento inválido. Email e token são necessários.');
        return;
      }

      try {
        // Use fetch directly since we need query params
        const response = await fetch(
          `https://cnjlujmmawqykzggvxjf.supabase.co/functions/v1/unsubscribe?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`
        );

        const result = await response.json();

        if (response.ok && result.success) {
          setStatus('success');
          setMessage('Você foi removido da nossa newsletter com sucesso.');
        } else if (response.status === 404) {
          setStatus('not-found');
          setMessage('Link de cancelamento inválido ou já utilizado.');
        } else {
          setStatus('error');
          setMessage(result.error || 'Ocorreu um erro ao processar sua solicitação.');
        }
      } catch (error) {
        console.error('Unsubscribe error:', error);
        setStatus('error');
        setMessage('Ocorreu um erro ao processar sua solicitação.');
      }
    };

    processUnsubscribe();
  }, [email, token]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Helmet>
        <title>Cancelar Inscrição | KopfAI Blog</title>
        <meta name="description" content="Cancele sua inscrição na newsletter do KopfAI Blog. Processamos seu pedido em segundos e você pode voltar a se inscrever quando quiser." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="/unsubscribe" />
        <meta property="og:title" content="Cancelar Inscrição | KopfAI Blog" />
        <meta property="og:description" content="Cancele sua inscrição na newsletter do KopfAI Blog." />
        <meta property="og:url" content="/unsubscribe" />
      </Helmet>
      <h1 className="sr-only">Cancelar inscrição na newsletter do KopfAI Blog</h1>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2">
            {status === 'loading' && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
            {status === 'success' && <CheckCircle className="h-6 w-6 text-green-500" />}
            {(status === 'error' || status === 'not-found') && <XCircle className="h-6 w-6 text-destructive" />}
            <span>
              {status === 'loading' && 'Processando...'}
              {status === 'success' && 'Inscrição cancelada'}
              {status === 'error' && 'Erro'}
              {status === 'not-found' && 'Email não encontrado'}
            </span>
          </CardTitle>
          <CardDescription>
            {message}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status !== 'loading' && (
            <>
              <p className="text-sm text-muted-foreground">
                {status === 'success' 
                  ? 'Sentiremos sua falta! Você pode se inscrever novamente a qualquer momento.'
                  : 'Se você tiver alguma dúvida, entre em contato conosco.'}
              </p>
              <Button asChild variant="outline">
                <Link to="/">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Voltar ao Blog
                </Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Unsubscribe;
